import React, { useRef, useEffect, useState, Fragment } from "react";
import { FormControl, Card, Layout, Button, usePath, useAppConfig } from "@fdoh/health-ui-react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { useSubmitForm } from "../../hooks/useSubmitForm";
import { useForm } from "react-hook-form";
import { AbandonmentInput, schema, AbandonmentFormProps, ComplianceData, SoilProfile, SoilProfileHorizon, SelectOption, SoilVerificationData } from "./AbandonmentForm.types";
import { useData } from "../../hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import CSS from 'csstype';
import { Constants, InspectionStatus } from "../../helpers/Constants";
import { toast } from "react-toastify";
import { InspectionStatusInput } from "../InspectionHome/InspectionHome.types";
import useProfile from "../../hooks/useProfile";
import { UserSecurityInput } from "../../components/DBProtectedRoute.types";
// import { usePrettyPrintedState } from './usePrettyPrintedState'

import SoilProfileEditor from "./SoilProfileEditor";
import SoilProfileTable from "./SoilProfileTable";


export const AbandonmentForm = (props: AbandonmentFormProps) => {
    const history = useHistory();
    const path = usePath();
    const complianceData = useData<ComplianceData[]>("Compliance") || [];
  
    const inspectionStatusData = useData<InspectionStatusInput>("inspectionDetails/" + props.applicationInspectionId);
    const config =  useAppConfig();
    const application = config.applications.filter(x => x.name === "privateProviderInspectionApi")[0];
         const user =  useProfile(application) as UserSecurityInput;
    // Initialize default soil profiles
    const defaultSoilProfiles: SoilProfile[] = [
        {
            profileNumber: 1,
            horizons: [],
            usdaSoilSeries: ""
        },
        {
            profileNumber: 2,
            horizons: [],
            usdaSoilSeries: ""
        }
    ];  
    
    
    
         const defaultvalues = { ...props.values,
             dateCreated: props.values?.dateCreated?.toLocaleString().slice(0, 10), 
             tankPumpedDate: props.values?.tankPumpedDate?.toLocaleString().slice(0, 10), 
             tankCrushedFilledDate: props.values?.tankCrushedFilledDate?.toLocaleString().slice(0, 10),
            soilProfiles: props.values?.soilProfiles || defaultSoilProfiles,
            // Let compliance fields be undefined initially - user must select a value
            tankPumpedComplianceId: props.values?.tankPumpedComplianceId,
            tankCrushedFilledComplianceId: props.values?.tankCrushedFilledComplianceId,
            soilVerificationComplianceId: props.values?.soilVerificationComplainceId,
            // Initialize new fields
            soilProfile1Depth: props.values?.soilProfile1Depth || undefined,
            soilProfile1DepthBenchmark: props.values?.soilProfile1DepthBenchmark || 0,
            soilProfile2Depth: props.values?.soilProfile2Depth || undefined,
            soilProfile2DepthBenchmark: props.values?.soilProfile2DepthBenchmark || 0,
            observedWaterTable: props.values?.observedWaterTable || undefined,
            observedWaterTableBenchmark: props.values?.observedWaterTableBenchmark || 0,
            estimatedWetSeasonWaterTableElevation: props.values?.estimatedWetSeasonWaterTableElevation || undefined,
            estimatedWetSeasonWaterTableElevationBenchmark: props.values?.estimatedWetSeasonWaterTableElevationBenchmark || 0
            }
    const { control, handleSubmit, setValue, watch, getValues, trigger, formState: { errors } } = useForm<AbandonmentInput>({
        resolver: yupResolver(schema),
        defaultValues: defaultvalues,
    });

    const [tankPumpedDate, settankPumpedDate] = useState('');
    const [tankCrushedFilledDate, settankCrushedFilledDate] = useState('');
    const [dateError, setDateError] = useState('');
    const [isDisabled, setIsDisabled] = useState(false);
    const today = new Date().toISOString().split('T')[0];

    // EHD lookup data
    const colorCodes = useData<SelectOption[]>("SoilVerification/lookup/munsell-colors") || [];
    const soilTextures = useData<SelectOption[]>("SoilVerification/lookup/soil-textures") || [];
    const usdaSoilSeries = useData<SelectOption[]>("SoilVerification/lookup/usda-soil-series") || [];


    // Existing soil verification data
    const existingSoilVerificationData = useData<SoilVerificationData>(`SoilVerification/abandonment/${props.applicationInspectionId}`) || null;

    const [soilProfiles, setSoilProfiles] = useState<SoilProfile[]>(defaultSoilProfiles);

    const [showSoilProfileEditor, setShowSoilProfileEditor] = useState(false);
    const [editingProfile, setEditingProfile] = useState<SoilProfile | null>(null);
    const [editingProfileTitle, setEditingProfileTitle] = useState("");

    // Benchmark options for above/below selection
    const benchmarkOptions = [
        {
            id: 0,
            name: "Select above or below"
        },
        {
            id: 1,
            name: "Above"
        },
        {
            id: 2,
            name: "Below"
        }
    ];

    let watchPumpedDate = watch("tankPumpedDate");
    let watchCrushedFilledDate = watch("tankCrushedFilledDate");


    // Effect to check if PumpDate is less than or equal to FillDate
    useEffect(() => {
        if (watchPumpedDate && watchCrushedFilledDate) {
            const pumpDate = new Date(watchPumpedDate);
            const crushedFilledDate = new Date(watchCrushedFilledDate);
            if (pumpDate > crushedFilledDate) {
                setDateError('Tank Pumped Date should be less than or equal to Tank Crushed and Filled Date.');
                setIsDisabled(true);
            } else {
                setDateError('');
                setIsDisabled(false);
            }
        }
    }, [watchPumpedDate, watchCrushedFilledDate]);

    // Load existing soil verification data when available
    useEffect(() => {
        if (existingSoilVerificationData && existingSoilVerificationData.soilProfiles.length > 0) {
            setSoilProfiles(existingSoilVerificationData.soilProfiles);
        }

    }, [existingSoilVerificationData]);

    // Update form validation when soil profiles change
    useEffect(() => {
        setValue('soilProfiles', soilProfiles);

        // Check if validation conditions are met and trigger validation to clear errors
        const complianceId = Number(watch("soilVerificationComplainceId"));
        if (complianceId === Constants.Out_of_Compliance) {
            // Check if any profile now meets validation requirements
            const hasValidProfiles = soilProfiles.some(profile =>
                profile.usdaSoilSeries && profile.usdaSoilSeries.trim() !== '' &&
                profile.horizons.length > 0 &&
                profile.horizons.every(horizon =>
                    horizon.munsellColor && horizon.soilTexture &&
                    horizon.depthFrom !== undefined && horizon.depthTo !== undefined
                )
            );

            if (hasValidProfiles) {
                // Trigger validation to clear errors if conditions are now met
                setTimeout(() => {
                    trigger('soilProfiles');
                }, 100);
            }
        }
    }, [soilProfiles, setValue, trigger, watch]);

    function timeout(delay: number) {
        return new Promise(res => setTimeout(res, delay));
    }
    const formRef = useRef<HTMLFormElement>(null);


    const onSubmit = useSubmitForm<AbandonmentInput>(
        'abandonment',
        async (response) => {
            toast.success("Abandonment Saved Successfully");
            await timeout(2000);
            history.push(props.returnPath);
        },
        props.action
    );

    const submit = async () => {
        setValue('applicationId', props.applicationId ? props.applicationId : 0);
        setValue('applicationInspectionId', props.applicationInspectionId ? props.applicationInspectionId : 0);

        // Set soil profile data
        setValue('soilProfiles', soilProfiles);

        // Ensure required date fields are set if they're not already
        const currentFormData = getValues();
        if (!currentFormData.tankPumpedDate && tankPumpedDate) {
            setValue('tankPumpedDate', tankPumpedDate);
        }
        if (!currentFormData.tankCrushedFilledDate && tankCrushedFilledDate) {
            setValue('tankCrushedFilledDate', tankCrushedFilledDate);
        }
        if (!currentFormData.dateCreated) {
            const currentDate = new Date().toISOString().slice(0, 10);
            setValue('dateCreated', currentDate);
        }

        // Trigger form validation and show errors if validation fails
        const isValid = await trigger();

        if (!isValid) {
            // Force validation display for soil profiles specifically
            await trigger('soilProfiles');
            await trigger('soilVerificationComplainceId');
            await trigger('soilVerificationComment');

            // Trigger validation for new fields
            await trigger('soilProfile1Depth');
            await trigger('soilProfile1DepthBenchmark');
            await trigger('soilProfile2Depth');
            await trigger('soilProfile2DepthBenchmark');
            await trigger('observedWaterTable');
            await trigger('observedWaterTableBenchmark');
            await trigger('estimatedWetSeasonWaterTableElevation');
            await trigger('estimatedWetSeasonWaterTableElevationBenchmark');

            // Also trigger validation for individual soil profile fields
            if (soilProfiles.length > 0) {
                for (let i = 0; i < soilProfiles.length; i++) {
                    await trigger(`soilProfiles.${i}.usdaSoilSeries`);
                    if (soilProfiles[i].horizons.length > 0) {
                        for (let j = 0; j < soilProfiles[i].horizons.length; j++) {
                            await trigger(`soilProfiles.${i}.horizons.${j}.munsellColor`);
                            await trigger(`soilProfiles.${i}.horizons.${j}.soilTexture`);
                            await trigger(`soilProfiles.${i}.horizons.${j}.depthFrom`);
                            await trigger(`soilProfiles.${i}.horizons.${j}.depthTo`);
                        }
                    }
                }
            }



            // Focus on the validation error alert if it exists
            setTimeout(() => {
                const errorAlert = document.querySelector('.alert-danger[role="alert"]');
                if (errorAlert) {
                    errorAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Add focus for accessibility
                    (errorAlert as HTMLElement).focus();
                }
            }, 100);
            return;
        }

        // Automatically save soil verification data to EHD when submitting
        if (props.applicationInspectionId && soilProfiles.some(profile => profile.horizons.length > 0)) {
            try {
                // Prepare soil profiles data with required backend fields
                const preparedSoilProfiles = soilProfiles
                    .filter(profile => profile.horizons.length > 0) // Only send profiles with horizons
                    .map(profile => ({
                        ...profile,
                        applicationInspectionId: props.applicationInspectionId,
                        createdBy: user?.userName || 'System',
                        dateCreated: new Date(),
                        horizons: profile.horizons.map(horizon => ({
                            ...horizon,
                            createdBy: user?.userName || 'System',
                            dateCreated: new Date()
                        }))
                    }));

                // Save to EHD first - controller expects just the soil profiles array
                await submitSoilVerificationData(preparedSoilProfiles);
            } catch (error) {

                toast.error("Failed to save soil verification data to EHD");
            }
        }

        if(inspectionStatusData?.abandonmentInspectionId == null 
            || inspectionStatusData?.abandonmentInspectionId == undefined){
                setValue('dateCreated', new Date());
                setValue('createdBy', user?.userName);
            }
            else {
                setValue('dateUpdated', new Date());
                setValue('updatedBy', user?.userName);
            }
        if (formRef.current) {

            formRef.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
            );

        }
    }

    // Save soil verification data
    const submitSoilVerificationData = useSubmitForm<SoilProfile[]>(
        `SoilVerification/abandonment/${props.applicationInspectionId}/save`,
        async (response) => {
            toast.success('Soil verification data saved successfully to EHD!');
        },
        'post'
    );

    let showTankPumpedComment = watch("tankPumpedComplianceId");
    let showTankCrushedFilledComment = watch("tankCrushedFilledComplianceId");
    let showSoilVerificationComment = watch("soilVerificationComplainceId");
    let markAllCompliance = watch("markAll");
    //setValue('applicationId', 1);

    let watchdate = watch("tankCrushedFilledDate");

    const h1Styles: CSS.Properties = {
        float: "right",
        marginTop: '27px'
    };
    const markClick = (e: any) => {
        setValue('tankPumpedComplianceId', markAllCompliance);
        setValue('tankCrushedFilledComplianceId', markAllCompliance);
        setValue('soilVerificationComplainceId', markAllCompliance);
      

        e.preventDefault();
    };

    const renumberHorizons = (horizons: SoilProfileHorizon[]): SoilProfileHorizon[] => {
        return horizons.map((horizon, index) => ({
            ...horizon,
            horizonNumber: index + 1
        }));
    };


    const handleAddSoilProfile = (profileNumber: number) => {
        const profile = soilProfiles.find(p => p.profileNumber === profileNumber);
        if (profile) {
            setEditingProfile(profile);
            setEditingProfileTitle(`Profile ${profileNumber} - Add New Horizon`);
            setShowSoilProfileEditor(true);
        }
    };

    const handleEditSoilProfile = (profileNumber: number, horizon: SoilProfileHorizon) => {
        const profile = soilProfiles.find(p => p.profileNumber === profileNumber);
        if (profile) {
            setEditingProfile(profile);
            setEditingProfileTitle(`Profile ${profileNumber} - Edit Horizon`);
            setShowSoilProfileEditor(true);
        }
    };

    const handleDeleteSoilProfile = (profileNumber: number, horizonIdentifier: number) => {
        setSoilProfiles(prev => prev.map(profile => {
            if (profile.profileNumber === profileNumber) {

                const filteredHorizons = profile.horizons.filter(h =>
                    h.id !== horizonIdentifier && h.horizonNumber !== horizonIdentifier
                );
                return {
                    ...profile,
                    horizons: renumberHorizons(filteredHorizons)
                };
            }
            return profile;
        }));
    };

    const handleUSDAChange = (profileNumber: number, value: string) => {
        setSoilProfiles(prev => prev.map(profile => {
            if (profile.profileNumber === profileNumber) {
                return { ...profile, usdaSoilSeries: value };
            }
            return profile;
        }));


        setTimeout(() => {
            trigger('soilProfiles');
            trigger(`soilProfiles.${profileNumber - 1}.usdaSoilSeries`);
        }, 100);
    };

    const handleSaveSoilProfile = (updatedProfile: SoilProfile) => {
        setSoilProfiles(prev => prev.map(profile => {
            if (profile.profileNumber === updatedProfile.profileNumber) {
                return {
                    ...updatedProfile,
                    horizons: renumberHorizons(updatedProfile.horizons)
                };
            }
            return profile;
        }));
        setShowSoilProfileEditor(false);
        setEditingProfile(null);


        setTimeout(() => {
            trigger('soilProfiles');
            trigger(`soilProfiles.${updatedProfile.profileNumber - 1}`);
        }, 100);
    };

    const handleCancelSoilProfile = () => {
        setShowSoilProfileEditor(false);
        setEditingProfile(null);
    };

    return (
        <form id={props.id} ref={formRef} onSubmit={handleSubmit((data: AbandonmentInput) => onSubmit(data))}>
            <style>{`
                .soil-verification-section .modern-section-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 16px;
                    padding: 28px;
                    margin-bottom: 24px;
                    color: white;
                    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
                }
                
                .soil-verification-section .header-content {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }
                
                .soil-verification-section .header-icon {
                    width: 48px;
                    height: 48px;
                    background: rgba(255, 255, 255, 0.15);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    backdrop-filter: blur(10px);
                }
                
                .soil-verification-section .header-text {
                    flex: 1;
                }
                
                .soil-verification-section .section-title {
                    margin: 0 0 8px 0;
                    font-size: 24px;
                    font-weight: 700;
                    line-height: 1.2;
                }
                
                .soil-verification-section .section-subtitle {
                    margin: 0;
                    opacity: 0.9;
                    font-size: 16px;
                    font-weight: 400;
                    line-height: 1.4;
                }
                
                .soil-verification-section .soil-profiles-container {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }
                

                
                @media (max-width: 768px) {
                    .soil-verification-section .modern-section-header {
                        padding: 20px;
                    }
                    
                    .soil-verification-section .header-content {
                        gap: 16px;
                    }
                    
                    .soil-verification-section .header-icon {
                        width: 40px;
                        height: 40px;
                    }
                    
                    .soil-verification-section .section-title {
                        font-size: 20px;
                    }
                    
                    .soil-verification-section .section-subtitle {
                        font-size: 14px;
                    }
                    

                }
            `}</style>


            <div className="row">
                <div className="col col-md-8">
                    <div className="card-main-title">
                        Abandonment / Soils Verification 
                    </div>                    
                </div>
                <div className="col col-md-3">
                    <FormControl.Select
                        control={control}
                        name="markAll"
                        label="Mark All"
                        options={complianceData}
                        required
                    />
                </div>
                <div className="col col-md-1" style={h1Styles}>
                    <Button name="markAll" className="btn btn-primary" onClick={markClick} >Apply</Button>
                </div>
            </div>

            <Card.Container>

                <Card.Header>
                    <Card.Title>{props.title}</Card.Title>
                </Card.Header>

                <Card.Body>
                    {/*  ============================================================================================================================================*/}
                    <Layout.Row>   <>
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="tankPumpedComplianceId"
                                    label="Tank Pumped"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Date
                                    control={control}
                                    name="tankPumpedDate"
                                    label="Tank Pumped Date"
                                    required= {showTankPumpedComment != Constants.Not_Applicable && showTankPumpedComment !=Constants.Unobserved }
                                />
                            </div>
                        </div>
                        {showTankPumpedComment &&
                            showTankPumpedComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tankPumpedComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="tankCrushedFilledComplianceId"
                                    label="Tank Crushed and Filled"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Date
                                    control={control}
                                    name="tankCrushedFilledDate"
                                    label="Tank Crushed and Filled or Removed Date"
                                    required= {showTankCrushedFilledComment != Constants.Not_Applicable && showTankCrushedFilledComment !=Constants.Unobserved }
                                />
                            </div>
                        </div>
                        {showTankCrushedFilledComment &&
                            showTankCrushedFilledComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tankCrushedFilledComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="soilVerificationComplainceId"
                                    label="Soil Verification (Documentation of Profile Required if Out of Compliance)"
                                    options={complianceData}
                                    required
                                />
                            </div>
                        </div>
                        {showSoilVerificationComment &&
                            showSoilVerificationComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="soilVerificationComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Soil Profile Information - Show when soil verification is out of compliance */}
                        {showSoilVerificationComment == 3 && (
                            <div className="soil-verification-section mt-4">
                                {/* Modern Section Header */}
                                <div className="modern-section-header">
                                    <div className="header-content">
                                        <div className="header-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div className="header-text">
                                            <h3 className="section-title">Soil Verification & Analysis</h3>
                                            <p className="section-subtitle">Professional soil profile documentation and analysis for compliance verification</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Soil Profiles Container */}
                                <div className="soil-profiles-container">
                                    {/* Profile 1 */}
                                    <SoilProfileTable
                                        profile={soilProfiles[0]}
                                        profileNumber={1}
                                        profileTitle="Primary Soil Profile"
                                        onEdit={(horizon) => handleEditSoilProfile(1, horizon)}
                                        onDelete={(horizonId) => handleDeleteSoilProfile(1, horizonId)}
                                        onAdd={() => handleAddSoilProfile(1)}
                                        onUSDAChange={(value) => handleUSDAChange(1, value)}
                                        usdaSoilSeries={soilProfiles[0].usdaSoilSeries}
                                        munsellColors={colorCodes}
                                        soilTextures={soilTextures}
                                        usdaSoilSeriesOptions={usdaSoilSeries}
                                        depthFieldName="soilProfile1Depth"
                                        benchmarkFieldName="soilProfile1DepthBenchmark"
                                        isRequired={Number(watch("soilVerificationComplainceId")) === Constants.Out_of_Compliance}
                                        control={control}
                                    />

                                    {/* Profile 2 */}
                                    <SoilProfileTable
                                        profile={soilProfiles[1]}
                                        profileNumber={2}
                                        profileTitle="Secondary Soil Profile"
                                        onEdit={(horizon) => handleEditSoilProfile(2, horizon)}
                                        onDelete={(horizonId) => handleDeleteSoilProfile(2, horizonId)}
                                        onAdd={() => handleAddSoilProfile(2)}
                                        onUSDAChange={(value) => handleUSDAChange(2, value)}
                                        usdaSoilSeries={soilProfiles[1].usdaSoilSeries}
                                        munsellColors={colorCodes}
                                        soilTextures={soilTextures}
                                        usdaSoilSeriesOptions={usdaSoilSeries}
                                        depthFieldName="soilProfile2Depth"
                                        benchmarkFieldName="soilProfile2DepthBenchmark"
                                        isRequired={Number(watch("soilVerificationComplainceId")) === Constants.Out_of_Compliance}
                                        control={control}
                                    />
                                </div>


                                {/* Water Table Fields */}
                                <div className="row mt-3">
                                    <div className="col col-md-6">
                                        <FormControl.Text
                                            control={control}
                                            name="observedWaterTable"
                                            label="Observed Water Table (inches)"
                                            type="number"
                                            required={Number(watch("soilVerificationComplainceId")) === Constants.Out_of_Compliance}
                                        />
                                    </div>
                                    <div className="col col-md-6">
                                        <FormControl.Select
                                            control={control}
                                            name="observedWaterTableBenchmark"
                                            label="Above or below benchmark?"
                                            options={benchmarkOptions}
                                            required={Number(watch("soilVerificationComplainceId")) === Constants.Out_of_Compliance}
                                        />
                                    </div>
                                </div>

                                <div className="row mt-3">
                                    <div className="col col-md-6">
                                        <FormControl.Text
                                            control={control}
                                            name="estimatedWetSeasonWaterTableElevation"
                                            label="Estimated Wet Season Water Table Elevation (inches)"
                                            type="number"
                                            required={Number(watch("soilVerificationComplainceId")) === Constants.Out_of_Compliance}
                                        />
                                    </div>
                                    <div className="col col-md-6">
                                        <FormControl.Select
                                            control={control}
                                            name="estimatedWetSeasonWaterTableElevationBenchmark"
                                            label="Above or below benchmark?"
                                            options={benchmarkOptions}
                                            required={Number(watch("soilVerificationComplainceId")) === Constants.Out_of_Compliance}
                                        />
                                    </div>
                                </div>

                                {/* Validation feedback for soil profiles */}
                                <div className="row mt-2">
                                    <div className="col col-md-12">
                                        {/* Display soil profile validation errors */}
                                        {Number(watch("soilVerificationComplainceId")) === Constants.Out_of_Compliance && errors.soilProfiles && (
                                            <div className="alert alert-danger" role="alert" tabIndex={-1}>
                                                <h6 className="mb-2"><strong>Soil Profile Validation Errors:</strong></h6>

                                                {/* General soil profiles error */}
                                                {errors.soilProfiles.message && (
                                                    <div className="mb-2">• {errors.soilProfiles.message}</div>
                                                )}

                                                {/* Individual profile errors */}
                                                {Array.isArray(errors.soilProfiles) && errors.soilProfiles.map((profileError: any, index: number) => (
                                                    <div key={index} className="mb-2">
                                                        <strong>Profile {index + 1}:</strong>

                                                        {/* USDA Soil Series error */}
                                                        {profileError?.usdaSoilSeries?.message && (
                                                            <div className="ms-3">• {profileError.usdaSoilSeries.message}</div>
                                                        )}

                                                        {/* Horizons error */}
                                                        {profileError?.horizons?.message && (
                                                            <div className="ms-3">• {profileError.horizons.message}</div>
                                                        )}

                                                        {/* Individual horizon field errors */}
                                                        {Array.isArray(profileError?.horizons) && profileError.horizons.map((horizonError: any, horizonIndex: number) => (
                                                            <div key={horizonIndex} className="ms-3">
                                                                <strong>Horizon {horizonIndex + 1}:</strong>
                                                                {horizonError?.munsellColor?.message && (
                                                                    <div className="ms-3">• {horizonError.munsellColor.message}</div>
                                                                )}
                                                                {horizonError?.soilTexture?.message && (
                                                                    <div className="ms-3">• {horizonError.soilTexture.message}</div>
                                                                )}
                                                                {horizonError?.depthFrom?.message && (
                                                                    <div className="ms-3">• {horizonError.depthFrom.message}</div>
                                                                )}
                                                                {horizonError?.depthTo?.message && (
                                                                    <div className="ms-3">• {horizonError.depthTo.message}</div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}

                                                {/* Additional field validation errors */}
                                                {errors.soilProfile1Depth?.message && (
                                                    <div className="mb-2">• {errors.soilProfile1Depth.message}</div>
                                                )}
                                                {errors.soilProfile1DepthBenchmark?.message && (
                                                    <div className="mb-2">• {errors.soilProfile1DepthBenchmark.message}</div>
                                                )}
                                                {errors.soilProfile2Depth?.message && (
                                                    <div className="mb-2">• {errors.soilProfile2Depth.message}</div>
                                                )}
                                                {errors.soilProfile2DepthBenchmark?.message && (
                                                    <div className="mb-2">• {errors.soilProfile2DepthBenchmark.message}</div>
                                                )}
                                                {errors.observedWaterTable?.message && (
                                                    <div className="mb-2">• {errors.observedWaterTable.message}</div>
                                                )}
                                                {errors.observedWaterTableBenchmark?.message && (
                                                    <div className="mb-2">• {errors.observedWaterTableBenchmark.message}</div>
                                                )}
                                                {errors.estimatedWetSeasonWaterTableElevation?.message && (
                                                    <div className="mb-2">• {errors.estimatedWetSeasonWaterTableElevation.message}</div>
                                                )}
                                                {errors.estimatedWetSeasonWaterTableElevationBenchmark?.message && (
                                                    <div className="mb-2">• {errors.estimatedWetSeasonWaterTableElevationBenchmark.message}</div>
                                                )}
                                            </div>
                                        )}


                                        {/* Hidden field for form validation registration */}
                                        <FormControl.Hidden
                                            control={control}
                                            name="soilProfiles"
                                            required={Number(watch("soilVerificationComplainceId")) === Constants.Out_of_Compliance}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                           
                            <div className="col col-md-4" hidden>
                                <FormControl.Text
                                    control={control}
                                    name="InspectionStatusId"
                                    label="InspectionStatusId"
                                    required
                                />
                                  <FormControl.Hidden
                                    control={control}
                                    name="applicationInspectionId"
                                    required
                                />
                            </div>

                        </div>
                        {/*  ============================================================================================================================================*/}
                    </></Layout.Row>
                </Card.Body>
                <Card.Footer>
                    <div className="d-flex">
                        <span className="btn btn-link" onClick={(e) => {
                            e.preventDefault();
                            setValue('inspectionStatusId', InspectionStatus.Incomplete);
                            submit()

                        }}><svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-circle-arrow-left" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18"></path>
                                <path d="M8 12l4 4"></path><path d="M8 12h8"></path>
                                <path d="M12 8l-4 4"></path>
                            </svg> Save and Exit
                        </span>
                        <div className="btn-list ms-auto">
                            <Link
                                to={props.returnPath ?? path.current}
                                className="btn btn-light"
                                role="button"
                            >
                                Cancel
                            </Link>
                            <Button className="btn btn-primary ms-auto" onClick={(e) => {
                                e.preventDefault();
                                setValue('inspectionStatusId', InspectionStatus.Complete);
                                submit()

                            }}>Next</Button>
                        </div>
                    </div>
                </Card.Footer>
            </Card.Container>
            
            {/* Soil Profile Editor Modal */}
            {editingProfile && (
                <SoilProfileEditor
                    profile={editingProfile}
                    onSave={handleSaveSoilProfile}
                    onCancel={handleCancelSoilProfile}
                    isVisible={showSoilProfileEditor}
                    title={editingProfileTitle}
                    colorCodes={colorCodes}
                    soilTextures={soilTextures}
                    usdaSoilSeriesOptions={usdaSoilSeries}
                />
            )}
        </form>
    );
};

export default AbandonmentForm;

